import { useState, useMemo, useEffect } from 'react';
import type { Dashboard, DashboardLayout, WidgetType, DisplayType } from '@/types/dashboard';
import { t } from '@/i18n';
import { cleanDashboardForSave } from '@/utils/persistence';
import { DEFAULT_PANEL_SIZES } from '@/utils/panelDefaults';
import { checkCondition } from '@/utils/api';
import './DashboardSettings.css';
import { VIEW_MODE_CONDITION_PRESETS, CUSTOM_PRESET } from '@/config/viewModeConditionPresets';

type SettingsTab = 'settings' | 'export';

export interface SaveSettingsOptions {
  layout: DashboardLayout;
  updatedTitle?: string;
  templateType?: 'generic' | 'specific';
  contextKey?: string;
  newViewMode?: string;
  viewModeCondition?: string;
  displayType?: DisplayType;
  updatedViewModeCondition?: string;
}

interface DashboardSettingsProps {
  dashboard: Dashboard;
  isDirty: boolean;
  contextKey?: string;
  onSave: (options: SaveSettingsOptions) => void;
  onDeleteSpecific?: () => void;
  onClose: () => void;
  conditionalFormError?: string | null;
}

const ALL_WIDGET_TYPES: WidgetType[] = ['indicator', 'labeled_list', 'chart', 'chat', 'entity_list'];

const WIDGET_LABEL_KEYS: Record<WidgetType, string> = {
  indicator: 'dashboard.widgetTypeIndicator',
  labeled_list: 'dashboard.widgetTypeLabeledList',
  chart: 'dashboard.widgetTypeChart',
  chat: 'dashboard.widgetTypeChat',
  entity_list: 'dashboard.widgetTypeEntityList',
};

const WIDGET_ICONS: Record<WidgetType, string> = {
  indicator: 'la-chart-bar',
  labeled_list: 'la-list-ul',
  chart: 'la-chart-line',
  chat: 'la-comment-dots',
  entity_list: 'la-th-list',
};

const SPACING_OPTIONS = [
  { value: 0, label: 'Без отступа' },
  { value: 1, label: 'Микро' },
  { value: 2, label: 'Маленький' },
  { value: 3, label: 'Средний' },
  { value: 4, label: 'Большой' },
  { value: 5, label: 'Очень большой' },
];

const COLUMN_OPTIONS = [8, 10, 12];

export function DashboardSettings({ dashboard, isDirty, contextKey, onSave, onDeleteSpecific, onClose, conditionalFormError }: DashboardSettingsProps) {
  const getInitialMargin = (index: number): number => {
    if (!dashboard.layout?.margin) return 10;
    return Array.isArray(dashboard.layout.margin) ? dashboard.layout.margin[index] : 10;
  };

  const [activeTab, setActiveTab] = useState<SettingsTab>('settings');
  const [columns, setColumns] = useState(dashboard.layout?.columns ?? 12);
  const [cellHeight, setCellHeight] = useState(dashboard.layout?.cellHeight ?? 70);
  const [marginH, setMarginH] = useState(getInitialMargin(0));
  const [marginV, setMarginV] = useState(getInitialMargin(1));
  const [listSpacing, setListSpacing] = useState(dashboard.layout?.listSpacing ?? 2);
  const [title, setTitle] = useState(dashboard.title ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const [panelSizes, setPanelSizes] = useState<Record<WidgetType, { w: number; h: number }>>(
    {
      ...DEFAULT_PANEL_SIZES,
      ...dashboard.layout.defaultPanelSizes,
    }
  );

  const getInitialTemplateType = (): 'generic' | 'specific' => {
    const parts = dashboard.sourceKey.split(':');
    return parts.length === 3 ? 'specific' : 'generic';
  };

  const [templateType, setTemplateType] = useState<'generic' | 'specific'>(getInitialTemplateType());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showConditionalForm, setShowConditionalForm] = useState(false);
  const [newViewMode, setNewViewMode] = useState('');
  const [viewModeCondition, setViewModeCondition] = useState('');
  const [selectedPresetKey, setSelectedPresetKey] = useState<string>(CUSTOM_PRESET.key);
  const [displayType, setDisplayType] = useState<'single' | 'list'>('single');
  const [testResult, setTestResult] = useState<unknown | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  const [isEditingFormula, setIsEditingFormula] = useState(false);
  const [editFormula, setEditFormula] = useState(dashboard.viewModeCondition || '');
  const [editPresetKey, setEditPresetKey] = useState<string>(CUSTOM_PRESET.key);
  const [formulaTestResult, setFormulaTestResult] = useState<unknown | null>(null);
  const [formulaTestError, setFormulaTestError] = useState<string | null>(null);
  const [isFormulaTesting, setIsFormulaTesting] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (conditionalFormError) {
      setErrorMessage(conditionalFormError);
      setFieldErrors({ newViewMode: conditionalFormError });
    } else {
      setErrorMessage(null);
      setFieldErrors({});
    }
  }, [conditionalFormError]);

  useEffect(() => {
    if (dashboard.viewModeCondition) {
      const matchedPreset = VIEW_MODE_CONDITION_PRESETS.find(
        p => p.formula === dashboard.viewModeCondition
      );
      setEditPresetKey(matchedPreset?.key ?? CUSTOM_PRESET.key);
    }
  }, [dashboard.viewModeCondition]);

  const exportJson = useMemo(() => JSON.stringify(cleanDashboardForSave(dashboard), null, 2), [dashboard]);

  const hasSizeChanges = ALL_WIDGET_TYPES.some(type => {
    const current = panelSizes[type];
    const initial = dashboard.layout.defaultPanelSizes?.[type] ?? DEFAULT_PANEL_SIZES[type];
    return current.w !== initial.w || current.h !== initial.h;
  });

  const hasTemplateTypeChange = templateType !== getInitialTemplateType();

  const hasChanges = title !== (dashboard.title ?? '')
    || columns !== (dashboard.layout?.columns ?? 12)
    || cellHeight !== (dashboard.layout?.cellHeight ?? 70)
    || marginH !== getInitialMargin(0)
    || marginV !== getInitialMargin(1)
    || listSpacing !== (dashboard.layout?.listSpacing ?? 2)
    || hasSizeChanges
    || hasTemplateTypeChange;

  const handleSave = async () => {
    if (cellHeight < 20 || cellHeight > 200) {
      alert('Высота ячейки должна быть от 20 до 200 пикселей.');
      return;
    }
    if (marginH < 0 || marginH > 50 || marginV < 0 || marginV > 50) {
      alert('Отступ должен быть от 0 до 50 пикселей.');
      return;
    }

    setIsSaving(true);
    try {
      const newLayout: DashboardLayout = {
        ...dashboard.layout,
        columns,
        cellHeight,
        margin: [marginH, marginV],
        listSpacing,
        defaultPanelSizes: panelSizes,
      };
      onSave({
        layout: newLayout,
        updatedTitle: title,
        templateType: hasTemplateTypeChange ? templateType : undefined,
        contextKey,
      });
    } catch (err) {
      console.error('Failed to save dashboard settings:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="dashboard-settings" data-testid="dashboard-settings">
      <div className="dashboard-settings__header">
        <h5 className="mb-0">{t('dashboard.settings')}</h5>
        <button type="button" className="btn-close" data-testid="dashboard-settings-close" onClick={onClose} />
      </div>

      <div className="dashboard-settings__tabs">
        <button
          type="button"
          className={`dashboard-settings__tab${activeTab === 'settings' ? ' dashboard-settings__tab--active' : ''}`}
          data-testid="dashboard-settings-tab-settings"
          onClick={() => setActiveTab('settings')}
        >
          {t('dashboard.settings')}
        </button>
        <button
          type="button"
          className={`dashboard-settings__tab${activeTab === 'export' ? ' dashboard-settings__tab--active' : ''}`}
          data-testid="dashboard-settings-tab-export"
          onClick={() => setActiveTab('export')}
        >
          {t('dashboard.export')}
        </button>
      </div>

      <div className="dashboard-settings__body" data-testid="dashboard-settings-body">
        {activeTab === 'settings' && (
          <>
            {isDirty && (
              <div className="dashboard-settings__lock-overlay" data-testid="dashboard-settings-lock">
                <div className="dashboard-settings__lock-message">
                  <i className="las la-exclamation-circle" style={{ fontSize: '2rem', marginBottom: '0.5rem', display: 'block' }}></i>
                  <p>Есть несохранённые изменения в панелях.</p>
                  <p>Сохраните изменения, чтобы получить доступ к настройкам дашборда.</p>
                </div>
              </div>
            )}

            {/* Card: Название */}
            <div className="settings-card">
              <div className="settings-card__header">
                <div className="settings-card__icon"><i className="las la-heading"></i></div>
                <div>
                  <h6 className="settings-card__title">Название</h6>
                  <p className="settings-card__desc">Название дашборда</p>
                </div>
              </div>
              <div className="settings-card__body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      data-testid="settings-title"
                      value={title}
                      disabled={isDirty}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                    <div className="form-text">Отображается в заголовке дашборда</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card: Сетка */}
            <div className="settings-card">
              <div className="settings-card__header">
                <div className="settings-card__icon"><i className="las la-th"></i></div>
                <div>
                  <h6 className="settings-card__title">Сетка</h6>
                  <p className="settings-card__desc">Настройка сетки панелей дашборда</p>
                </div>
              </div>
              <div className="settings-card__body">
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">Высота ячейки</label>
                    <input
                      type="number"
                      className="form-control"
                      data-testid="settings-cell-height"
                      min={20}
                      max={200}
                      step={5}
                      value={cellHeight}
                      disabled={isDirty}
                      onChange={(e) => setCellHeight(Number(e.target.value))}
                    />
                    <div className="form-text">Высота одной строки сетки в пикселях (20–200). Меньше — компактнее, больше — свободнее.</div>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Горизонтальный отступ</label>
                    <input
                      type="number"
                      className="form-control"
                      data-testid="settings-margin-h"
                      min={0}
                      max={50}
                      value={marginH}
                      disabled={isDirty}
                      onChange={(e) => setMarginH(Number(e.target.value))}
                    />
                    <div className="form-text">Расстояние между панелями по горизонтали в пикселях (0–50).</div>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Вертикальный отступ</label>
                    <input
                      type="number"
                      className="form-control"
                      data-testid="settings-margin-v"
                      min={0}
                      max={50}
                      value={marginV}
                      disabled={isDirty}
                      onChange={(e) => setMarginV(Number(e.target.value))}
                    />
                    <div className="form-text">Расстояние между панелями по вертикали в пикселях (0–50).</div>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Колонок</label>
                    <select
                      className="form-select"
                      data-testid="settings-columns"
                      value={columns}
                      disabled={isDirty}
                      onChange={(e) => setColumns(Number(e.target.value))}
                    >
                      {COLUMN_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    <div className="form-text">Количество колонок в сетке (8, 10, 12).</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card: Режим списка */}
            <div className="settings-card">
              <div className="settings-card__header">
                <div className="settings-card__icon"><i className="las la-list"></i></div>
                <div>
                  <h6 className="settings-card__title">Режим списка</h6>
                  <p className="settings-card__desc">Настройки отображения в режиме списка</p>
                </div>
              </div>
              <div className="settings-card__body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Отступ между панелями в списке</label>
                    <select
                      className="form-select"
                      data-testid="settings-list-spacing"
                      value={listSpacing}
                      disabled={isDirty}
                      onChange={(e) => setListSpacing(Number(e.target.value))}
                    >
                      {SPACING_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <div className="form-text">Влияет только на режим списка</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card: Размеры панелей */}
            <div className="settings-card" style={{ position: 'relative' }}>
              <div className="settings-card__header">
                <div className="settings-card__icon"><i className="las la-arrows-alt"></i></div>
                <div>
                  <h6 className="settings-card__title">Размеры панелей</h6>
                  <p className="settings-card__desc">Размеры по умолчанию для новых панелей каждого типа</p>
                </div>
              </div>
              <div className="settings-card__body">
                <table className="panel-sizes-table">
                  <thead>
                    <tr>
                      <th>Тип виджета</th>
                      <th style={{ width: '7rem' }}>Ширина</th>
                      <th style={{ width: '7rem' }}>Высота</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ALL_WIDGET_TYPES.map(type => {
                      const size = panelSizes[type];
                      return (
                        <tr key={type}>
                          <td>
                            <span className="widget-name">
                              <i className={`las ${WIDGET_ICONS[type]}`}></i>
                              {t(WIDGET_LABEL_KEYS[type])}
                            </span>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              data-testid={`settings-panel-size-w-${type}`}
                              min={1}
                              max={12}
                              value={size.w}
                              disabled={isDirty}
                              onChange={(e) => setPanelSizes(prev => ({
                                ...prev,
                                [type]: { ...prev[type], w: Number(e.target.value) }
                              }))}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              data-testid={`settings-panel-size-h-${type}`}
                              min={1}
                              max={10}
                              value={size.h}
                              disabled={isDirty}
                              onChange={(e) => setPanelSizes(prev => ({
                                ...prev,
                                [type]: { ...prev[type], h: Number(e.target.value) }
                              }))}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="form-text-small mt-2">
                  <i className="las la-info-circle me-1"></i>
                  Изменения применяются только к новым панелям
                </div>
              </div>
            </div>

            {/* Card: Тип шаблона */}
            {contextKey && (
              <div className="settings-card" style={{ position: 'relative' }}>
                <div className="settings-card__header">
                  <div className="settings-card__icon"><i className="las la-cubes"></i></div>
                  <div>
                    <h6 className="settings-card__title">{t('dashboard.templateType.title')}</h6>
                    <p className="settings-card__desc">{t('dashboard.templateType.desc')}</p>
                  </div>
                </div>
                <div className="settings-card__body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <select
                        className="form-select"
                        data-testid="settings-template-type"
                        value={templateType}
                        disabled={isDirty || getInitialTemplateType() === 'specific'}
                        onChange={(e) => setTemplateType(e.target.value as 'generic' | 'specific')}
                      >
                        {getInitialTemplateType() === 'generic' ? (
                          <>
                            <option value="generic">{t('dashboard.templateType.generic')}</option>
                            <option value="specific">{t('dashboard.templateType.specific')}</option>
                          </>
                        ) : (
                          <option value="specific">{t('dashboard.templateType.specific')}</option>
                        )}
                      </select>
                    </div>
                  </div>
                  {getInitialTemplateType() === 'specific' && (
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn btn-danger"
                        data-testid="settings-delete-specific-btn"
                        disabled={isDirty}
                        onClick={() => setShowDeleteConfirm(true)}
                      >
                        <i className="las la-trash"></i> {t('dashboard.templateType.deleteSpecific')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Card: Условный view_mode */}
            {contextKey && getInitialTemplateType() === 'generic' && (
              <div className="settings-card" style={{ position: 'relative' }}>
                <div className="settings-card__header">
                  <div className="settings-card__icon"><i className="las la-eye"></i></div>
                  <div>
                    <h6 className="settings-card__title">{t('dashboard.viewModeCondition.title')}</h6>
                    <p className="settings-card__desc">{t('dashboard.viewModeCondition.desc')}</p>
                  </div>
                </div>
                <div className="settings-card__body">
                  {!showConditionalForm ? (
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      data-testid="settings-create-conditional-btn"
                      disabled={isDirty}
                      onClick={() => setShowConditionalForm(true)}
                    >
                      <i className="las la-plus"></i> Создать копию с новым view_mode
                    </button>
                  ) : (
                    <div className="conditional-form">
                      {errorMessage && (
                        <div className="alert alert-danger" data-testid="settings-error" role="alert">
                          {errorMessage}
                        </div>
                      )}
                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label className="form-label">View mode</label>
                          <input
                            type="text"
                            className={`form-control${fieldErrors.newViewMode ? ' is-invalid' : ''}`}
                            data-testid="settings-view-mode-name"
                            placeholder="process_main_full"
                            value={newViewMode}
                            onChange={(e) => setNewViewMode(e.target.value)}
                          />
                          {fieldErrors.newViewMode && (
                            <div className="invalid-feedback">{fieldErrors.newViewMode}</div>
                          )}
                          <div className="form-text">Название view mode (латиница, без пробелов)</div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">{t('dashboard.displayType.title')}</label>
                        <div className="d-flex gap-3">
                          <label className="form-check">
                            <input type="radio" name="displayType" value="single" checked={displayType === 'single'} onChange={() => setDisplayType('single')} />
                            {t('dashboard.displayType.single')}
                          </label>
                          <label className="form-check">
                            <input type="radio" name="displayType" value="list" checked={displayType === 'list'} onChange={() => setDisplayType('list')} />
                            {t('dashboard.displayType.list')}
                          </label>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Формула-условие</label>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <select
                              className="form-select"
                              data-testid="settings-view-mode-preset"
                              value={selectedPresetKey}
                              onChange={(e) => {
                                const key = e.target.value;
                                setSelectedPresetKey(key);
                                if (key !== CUSTOM_PRESET.key) {
                                  const preset = VIEW_MODE_CONDITION_PRESETS.find(p => p.key === key);
                                  if (preset) {
                                    setViewModeCondition(preset.formula);
                                  }
                                } else {
                                  setViewModeCondition('');
                                }
                              }}
                            >
                              {VIEW_MODE_CONDITION_PRESETS.map(preset => (
                                <option key={preset.key} value={preset.key}>{preset.label}</option>
                              ))}
                              <option value={CUSTOM_PRESET.key}>{CUSTOM_PRESET.label}</option>
                            </select>
                          </div>
                        </div>
                        {selectedPresetKey === CUSTOM_PRESET.key && (
                          <textarea
                            className="form-control font-monospace mt-2"
                            data-testid="settings-view-mode-formula"
                            rows={3}
                            placeholder={"in_array('Бюджет', this.field_tag)"}
                            value={viewModeCondition}
                            onChange={(e) => setViewModeCondition(e.target.value)}
                          />
                        )}
                        <div className="form-text mt-2">DSL-формула. Если TRUE — будет показан этот дашборд.</div>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        <button
                          type="button"
                          className="btn btn-soft-secondary"
                          data-testid="settings-test-formula-btn"
                          disabled={isTesting || !viewModeCondition}
                          onClick={async () => {
                            setIsTesting(true);
                            setTestResult(null);
                            setTestError(null);
                            try {
                              const entityUuid = (contextKey && contextKey.includes(':') ? contextKey.split(':').pop() ?? contextKey : contextKey) || '';
                              const res = await checkCondition(viewModeCondition, entityUuid);
                              setTestResult(res.result);
                            } catch (err) {
                              setTestError(err instanceof Error ? err.message : 'Ошибка проверки');
                            } finally {
                              setIsTesting(false);
                            }
                          }}
                        >
                          {isTesting ? 'Проверка...' : 'Проверить формулу'}
                        </button>
                        {testResult !== null && (
                          <pre className="mt-2 p-2 bg-light rounded font-monospace small" data-testid="formula-test-raw-result">
                            {JSON.stringify(testResult, null, 2)}
                          </pre>
                        )}
                        {testError && (
                          <span className="text-danger small">{testError}</span>
                        )}
                      </div>
                      <div className="mt-3 d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-primary"
                          data-testid="settings-create-conditional-save"
                          disabled={!newViewMode || !viewModeCondition.trim()}
                          onClick={() => {
                            onSave({
                              layout: dashboard.layout,
                              newViewMode,
                              viewModeCondition: viewModeCondition.trim(),
                              displayType,
                              contextKey,
                            });
                          }}
                        >
                          <i className="las la-save"></i> Создать
                        </button>
                        <button
                          type="button"
                          className="btn btn-soft-secondary"
                          data-testid="settings-create-conditional-cancel"
                          onClick={() => {
                            setShowConditionalForm(false);
                            setNewViewMode('');
                            setViewModeCondition('');
                            setSelectedPresetKey(CUSTOM_PRESET.key);
                            setTestResult(null);
                            setTestError(null);
                            setErrorMessage(null);
                            setFieldErrors({});
                          }}
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Card: Формула-условие (редактирование) */}
            {dashboard.viewModeCondition && (
              <div className="settings-card" style={{ position: 'relative' }}>
                <div className="settings-card__header">
                  <div className="settings-card__icon"><i className="las la-code"></i></div>
                  <div>
                    <h6 className="settings-card__title">{t('dashboard.viewModeCondition.title')}</h6>
                    <p className="settings-card__desc">{t('dashboard.viewModeCondition.desc')}</p>
                  </div>
                </div>
                <div className="settings-card__body">
                  <div className="mb-3">
                    <label className="form-label">Формула-условие</label>
                    {isEditingFormula && (
                      <div className="row g-3">
                        <div className="col-md-6">
                          <select
                            className="form-select"
                            data-testid="settings-edit-preset"
                            value={editPresetKey}
                            onChange={(e) => {
                              const key = e.target.value;
                              setEditPresetKey(key);
                              if (key !== CUSTOM_PRESET.key) {
                                const preset = VIEW_MODE_CONDITION_PRESETS.find(p => p.key === key);
                                if (preset) {
                                  setEditFormula(preset.formula);
                                }
                              }
                            }}
                          >
                            {VIEW_MODE_CONDITION_PRESETS.map(preset => (
                              <option key={preset.key} value={preset.key}>{preset.label}</option>
                            ))}
                            <option value={CUSTOM_PRESET.key}>{CUSTOM_PRESET.label}</option>
                          </select>
                        </div>
                      </div>
                    )}
                    {(!isEditingFormula || editPresetKey === CUSTOM_PRESET.key) && (
                      <textarea
                        className={`form-control font-monospace${isEditingFormula ? ' mt-2' : ''}`}
                        data-testid="settings-edit-formula"
                        rows={3}
                        readOnly={!isEditingFormula}
                        value={isEditingFormula ? editFormula : dashboard.viewModeCondition}
                        onChange={(e) => setEditFormula(e.target.value)}
                      />
                    )}
                  </div>
                  <div className="d-flex gap-2 flex-wrap align-items-center">
                    {!isEditingFormula ? (
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        data-testid="settings-edit-formula-btn"
                        onClick={() => {
                          setIsEditingFormula(true);
                          setEditFormula(dashboard.viewModeCondition || '');
                        }}
                      >
                        <i className="las la-pen"></i> {t('dashboard.edit')}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-soft-secondary"
                          data-testid="settings-test-formula-edit-btn"
                          disabled={isFormulaTesting || !editFormula.trim()}
                          onClick={async () => {
                            setIsFormulaTesting(true);
                            setFormulaTestResult(null);
                            setFormulaTestError(null);
                            try {
                              const entityUuid = (contextKey && contextKey.includes(':') ? contextKey.split(':').pop() ?? contextKey : contextKey) || '';
                              const res = await checkCondition(editFormula.trim(), entityUuid);
                              setFormulaTestResult(res.result);
                            } catch (err) {
                              setFormulaTestError(err instanceof Error ? err.message : 'Ошибка проверки');
                            } finally {
                              setIsFormulaTesting(false);
                            }
                          }}
                        >
                          {isFormulaTesting ? 'Проверка...' : 'Проверить формулу'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          data-testid="settings-save-formula-btn"
                          disabled={!editFormula.trim()}
                          onClick={() => {
                            onSave({
                              layout: dashboard.layout,
                              updatedViewModeCondition: editFormula.trim(),
                            });
                            setIsEditingFormula(false);
                          }}
                        >
                          <i className="las la-save"></i> Сохранить формулу
                        </button>
                        <button
                          type="button"
                          className="btn btn-soft-secondary"
                          data-testid="settings-cancel-edit-formula-btn"
                          onClick={() => {
                            setIsEditingFormula(false);
                            setEditFormula(dashboard.viewModeCondition || '');
                            setEditPresetKey(CUSTOM_PRESET.key);
                            setFormulaTestResult(null);
                            setFormulaTestError(null);
                          }}
                        >
                          {t('dashboard.cancel')}
                        </button>
                      </>
                    )}
                  </div>
                  {formulaTestResult !== null && (
                    <div className="mt-2">
                      <pre className="p-2 bg-light rounded font-monospace small" data-testid="formula-test-raw-result">
                        {JSON.stringify(formulaTestResult, null, 2)}
                      </pre>
                    </div>
                  )}
                  {formulaTestError && (
                    <div className="mt-2 text-danger small">{formulaTestError}</div>
                  )}
                </div>
              </div>
            )}

            {showDeleteConfirm && (
              <div className="modal d-block" data-testid="delete-specific-modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog">
                  <div className="modal-content">
                    <div className="modal-body">
                      <p>{t('dashboard.confirmDeleteSpecific')}</p>
                    </div>
                    <div className="modal-footer">
                      <button className="btn btn-secondary" data-testid="delete-specific-cancel-btn" onClick={() => setShowDeleteConfirm(false)}>
                        {t('dashboard.cancel')}
                      </button>
                      <button className="btn btn-danger" data-testid="delete-specific-confirm-btn" onClick={() => { setShowDeleteConfirm(false); onDeleteSpecific?.(); }}>
                        {t('dashboard.delete')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        {activeTab === 'export' && (
          <div className="dashboard-settings__export">
            {isDirty && (
              <div className="dashboard-settings__lock-overlay" data-testid="dashboard-settings-export-lock">
                <div className="dashboard-settings__lock-message">
                  <i className="las la-exclamation-circle" style={{ fontSize: '2rem', marginBottom: '0.5rem', display: 'block' }}></i>
                  <p>{t('dashboard.exportUnsavedChanges')}</p>
                </div>
              </div>
            )}
            <textarea
              className="dashboard-settings__export-json"
              data-testid="dashboard-settings-export-json"
              readOnly
              value={exportJson}
            />
          </div>
        )}
      </div>

      <div className="dashboard-settings__footer">
        {activeTab === 'settings' && (
          <>
            <button
              type="button"
              className="btn btn-soft-secondary"
              data-testid="dashboard-settings-cancel"
              onClick={onClose}
            >
              {t('dashboard.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              data-testid="dashboard-settings-save"
              disabled={isDirty || isSaving || !hasChanges}
              onClick={handleSave}
            >
              {isSaving ? 'Сохранение...' : t('dashboard.save')}
            </button>
          </>
        )}
        {activeTab === 'export' && (
          <>
            <button
              type="button"
              className={`btn ${copySuccess ? 'btn-success' : 'btn-primary'}`}
              data-testid="dashboard-settings-export-copy"
              disabled={isDirty}
              onClick={() => {
                const textarea = document.querySelector<HTMLTextAreaElement>('[data-testid="dashboard-settings-export-json"]');
                if (textarea) {
                  textarea.select();
                  document.execCommand('copy');
                  setCopySuccess(true);
                  setTimeout(() => setCopySuccess(false), 2000);
                }
              }}
            >
              <i className={`las ${copySuccess ? 'la-check' : 'la-copy'}`}></i> {copySuccess ? t('dashboard.copied') : t('dashboard.copy')}
            </button>
            <button
              type="button"
              className="btn btn-soft-secondary"
              data-testid="dashboard-settings-export-cancel"
              onClick={onClose}
            >
              {t('dashboard.cancel')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
